import {
  AlertDialog as AlertDialogUI,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import React, { PropsWithChildren, ReactNode, useEffect, useRef } from 'react';
import * as AlertDialogPrimitive from '@radix-ui/react-alert-dialog';
import { twMerge } from 'tailwind-merge';
import Typo from '../Typo';
import { Loader2Icon } from 'lucide-react';
import { BUTTON_CONST } from '@/src/constants/buttons';

export interface AlertDialogProps {
  open: boolean;
  onOpenChange?: (open: boolean) => void;
  title?: ReactNode | string;
  cancelText?: string;
  onCancel?: () => void;
  okText?: string;
  onOk?: () => void;
  footer?: ReactNode;

  rootProps?: React.ComponentPropsWithoutRef<typeof AlertDialogPrimitive.Root>;
  contentProps?: React.ComponentPropsWithoutRef<
    typeof AlertDialogPrimitive.Content
  >;
  titleProps?: React.ComponentPropsWithoutRef<
    typeof AlertDialogPrimitive.Title
  >;
  descriptionProps?: React.ComponentPropsWithoutRef<
    typeof AlertDialogPrimitive.Description
  >;
  headerProps?: React.HTMLAttributes<HTMLDivElement>;
  footerProps?: React.HTMLAttributes<HTMLDivElement>;
  cancelButtonProps?: React.ComponentPropsWithoutRef<
    typeof AlertDialogPrimitive.Cancel
  >;
  okButtonProps?: React.ComponentPropsWithoutRef<
    typeof AlertDialogPrimitive.Action
  > & { isLoading?: boolean };
}

function AlertDialog({
  open,
  onOpenChange,
  title,
  cancelText,
  onCancel,
  okText,
  onOk,
  footer,
  children,
  rootProps,
  contentProps,
  headerProps,
  titleProps,
  descriptionProps,
  footerProps,
  cancelButtonProps,
  okButtonProps,
}: PropsWithChildren<AlertDialogProps>) {
  const okButtonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    const handleKeyDown = () => {
      if (open && okButtonRef?.current) {
        okButtonRef.current?.focus();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [open]);

  const renderFooterAlertDialog = () => {
    if (footer) {
      return footer;
    }

    return (
      <AlertDialogFooter {...footerProps}>
        <AlertDialogCancel
          className="transition-color disabled:opacity-50border active: inline-flex items-center justify-center rounded-md  border-gray1 bg-white bg-opacity-10 text-b14m text-gray9 hover:translate-y-[-2px] hover:border-gray2 hover:bg-gray1 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none"
          onClick={() => onCancel?.()}
          {...cancelButtonProps}
        >
          {cancelText ?? BUTTON_CONST.cancel}
        </AlertDialogCancel>
        <AlertDialogAction
          ref={okButtonRef}
          onClick={() => {
            onOk?.();
          }}
          className="transition-color disabled:opacity-50border active: inline-flex items-center justify-center rounded-md  border-primary bg-primary  text-b14m text-white hover:translate-y-[-2px] hover:border-primary500 hover:bg-primary500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 active:bg-blue8 disabled:pointer-events-none"
          {...okButtonProps}
          disabled={okButtonProps?.disabled || okButtonProps?.isLoading}
        >
          {okButtonProps?.isLoading && (
            <Loader2Icon size={16} className="mr-2 animate-spin" />
          )}{' '}
          {okText ?? BUTTON_CONST.ok}
        </AlertDialogAction>
      </AlertDialogFooter>
    );
  };

  const renderAlertDialogTitle = () => {
    if (typeof title === 'string') {
      return (
        <AlertDialogTitle {...titleProps}>
          <Typo typoType="b18sb">{title}</Typo>
        </AlertDialogTitle>
      );
    }

    return title;
  };

  return (
    <AlertDialogUI open={open} onOpenChange={onOpenChange} {...rootProps}>
      <AlertDialogContent
        className="z-alertDialog"
        {...contentProps}
        onKeyDown={e => {
          if (e.code === 'Enter') {
            onOk?.();
          }
          if (e.code === 'Escape') {
            onCancel?.();
          }
        }}
      >
        <AlertDialogHeader {...headerProps}>
          {renderAlertDialogTitle()}

          <AlertDialogDescription
            className={twMerge(
              'text-b16r text-gray5',
              descriptionProps?.className,
            )}
            {...descriptionProps}
          >
            {children}
          </AlertDialogDescription>
        </AlertDialogHeader>

        {renderFooterAlertDialog()}
      </AlertDialogContent>
    </AlertDialogUI>
  );
}

export default AlertDialog;
